import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { FiSearch, FiHeart, FiShoppingBag, FiUser, FiMenu, FiX } from 'react-icons/fi';
import useAuthStore from '../store/useAuthStore';
import useCartStore from '../store/useCartStore';
import useWishlistStore from '../store/useWishlistStore';
import { AuroraBlobs, GrainOverlay, GlobalUiStyles, GlassChip } from './ui';

const NAV_CATS = [
  { label: 'New Arrivals', href: '/category/new' },
  { label: 'Cotton', href: '/category/cotton' },
  { label: 'Printed', href: '/category/printed' },
  { label: 'Anarkali', href: '/category/anarkali' },
  { label: 'Office Wear', href: '/category/office' },
  { label: 'Party Wear', href: '/category/party' },
];

const WHATSAPP = process.env.REACT_APP_WHATSAPP_NUMBER || '919999999999';

export default function Layout() {
  const { user } = useAuthStore();
  const { cart, fetchCart } = useCartStore();
  const { fetchWishlist, ids: wishlistIds } = useWishlistStore();
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const { scrollY } = useScroll();
  const headerBg = useTransform(scrollY, [0, 80], ['rgba(250,246,238,0.55)', 'rgba(250,246,238,0.85)']);
  const headerBorder = useTransform(scrollY, [0, 80], ['rgba(229,219,200,0)', 'rgba(229,219,200,0.6)']);
  const headerShadow = useTransform(scrollY, [0, 80], ['0 0 0 rgba(0,0,0,0)', '0 8px 24px rgba(42,31,26,0.06)']);

  useEffect(() => {
    if (user) { fetchCart(); fetchWishlist(); }
  }, [user]);

  useEffect(() => { setMobileOpen(false); }, [location]);

  const cartCount = cart?.items?.length || 0;
  const wishCount = wishlistIds?.size || 0;
  const isHome = location.pathname === '/';

  return (
    <>
      <GlobalUiStyles />

      {/* Promo bar */}
      <div style={{ background: 'var(--ink)', color: '#EDE3D1', padding: '9px 24px', display: 'flex', justifyContent: 'center', gap: 36, fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', fontWeight: 600 }}>
        <span><span style={{ color: 'var(--gold)' }}>✦</span> Free shipping ₹999+</span>
        <span><span style={{ color: 'var(--gold)' }}>✦</span> 7-day returns</span>
        <span><span style={{ color: 'var(--gold)' }}>✦</span> COD across India</span>
      </div>

      <motion.header style={{
        position: 'sticky', top: 0, zIndex: 50,
        background: headerBg,
        backdropFilter: 'blur(18px) saturate(160%)',
        WebkitBackdropFilter: 'blur(18px) saturate(160%)',
        borderBottom: '1px solid',
        borderBottomColor: headerBorder,
        boxShadow: headerShadow,
      }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 24px', display: 'flex', alignItems: 'center', height: 72, gap: 32 }}>
          {/* Logo */}
          <Link to="/" style={{ textDecoration: 'none', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span className="serif" style={{ fontSize: 28, color: 'var(--primary)', letterSpacing: '-0.01em', lineHeight: 1 }}>VritiEthnic</span>
            <span className="hindi" style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1, letterSpacing: '0.05em' }}>वृति</span>
          </Link>

          {/* Desktop nav */}
          <nav className="desktop-nav" style={{ display: 'flex', gap: 4, flex: 1, justifyContent: 'center' }}>
            {NAV_CATS.map(cat => {
              const active = location.pathname === cat.href;
              return (
                <Link key={cat.href} to={cat.href} className="nav-link" style={{
                  padding: '8px 14px', fontSize: 12, fontWeight: 600, letterSpacing: '0.12em',
                  textTransform: 'uppercase', textDecoration: 'none', borderRadius: 999, whiteSpace: 'nowrap',
                  color: active ? 'var(--ink)' : 'var(--ink-2)',
                  background: active ? 'rgba(42,31,26,0.06)' : 'transparent',
                  position: 'relative', transition: 'background 0.2s ease, color 0.2s ease',
                }}>
                  {cat.label}
                </Link>
              );
            })}
          </nav>

          {/* Actions */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
            <IconBtn onClick={() => navigate('/search')} label="Search"><FiSearch size={19} /></IconBtn>
            <IconBtn onClick={() => navigate(user ? '/wishlist' : '/login')} label="Wishlist" count={wishCount}><FiHeart size={19} /></IconBtn>
            <IconBtn onClick={() => navigate('/cart')} label="Bag" count={cartCount}><FiShoppingBag size={19} /></IconBtn>
            <IconBtn onClick={() => navigate(user ? '/account' : '/login')} label="Account"><FiUser size={19} /></IconBtn>
            <button className="mobile-menu-btn" onClick={() => setMobileOpen(!mobileOpen)} style={{ display: 'none', border: 'none', background: 'transparent', padding: 8, color: 'var(--ink)', cursor: 'pointer' }}>
              {mobileOpen ? <FiX size={22} /> : <FiMenu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile nav drawer */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.nav
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
              style={{ overflow: 'hidden', borderTop: '1px solid var(--line-2)', background: 'rgba(250,246,238,0.95)', backdropFilter: 'blur(12px)' }}
            >
              <div style={{ padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                {NAV_CATS.map(cat => (
                  <Link key={cat.href} to={cat.href} style={{ padding: '12px 0', fontSize: 15, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ink)', textDecoration: 'none', borderBottom: '1px solid var(--line-2)' }}>
                    {cat.label}
                  </Link>
                ))}
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </motion.header>

      <main style={{ minHeight: 'calc(100vh - 200px)' }}>
        <Outlet />
      </main>

      {/* Footer */}
      <footer style={{ position: 'relative', background: '#1A100E', color: '#EDE3D1', marginTop: isHome ? 0 : 80, overflow: 'hidden' }}>
        <AuroraBlobs muted />
        <GrainOverlay />
        <div style={{ position: 'relative', zIndex: 2, maxWidth: 1280, margin: '0 auto', padding: '64px 24px 32px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2.2fr 1fr 1fr 1fr', gap: 56 }} className="footer-grid">
            <div>
              <Link to="/" style={{ textDecoration: 'none', display: 'inline-flex', flexDirection: 'column', gap: 4, marginBottom: 16 }}>
                <span className="serif" style={{ fontSize: 32, color: '#FFF', letterSpacing: '-0.01em', lineHeight: 1 }}>VritiEthnic</span>
                <span className="hindi" style={{ fontSize: 14, color: 'var(--gold)', lineHeight: 1 }}>वृति</span>
              </Link>
              <p style={{ fontSize: 13, lineHeight: 1.7, color: 'rgba(237,227,209,0.65)', maxWidth: 360, marginBottom: 20 }}>
                Handcrafted kurtis from the looms of Rajasthan. Each piece carries its maker's signature on the inside hem.
              </p>
              <a href={`https://wa.me/${WHATSAPP}`} target="_blank" rel="noreferrer" style={{
                display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 18px',
                background: 'rgba(37,211,102,0.16)', border: '1px solid rgba(37,211,102,0.32)',
                color: '#25D366', borderRadius: 999, fontSize: 12, fontWeight: 700,
                textDecoration: 'none', letterSpacing: '0.1em', textTransform: 'uppercase',
                backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
              }}>
                <span>WhatsApp Support</span>
                <span>↗</span>
              </a>
            </div>

            {[
              { title: 'Shop', links: [{ label: 'New Arrivals', href: '/category/new' }, { label: 'Cotton', href: '/category/cotton' }, { label: 'Anarkali', href: '/category/anarkali' }, { label: 'Office Wear', href: '/category/office' }] },
              { title: 'Account', links: [{ label: 'My Orders', href: '/orders' }, { label: 'Wishlist', href: '/wishlist' }, { label: 'Profile', href: '/account' }] },
              { title: 'Help', links: [{ label: 'Returns', href: '/returns' }, { label: 'Privacy', href: '/privacy' }, { label: 'Terms', href: '/terms' }] },
            ].map(col => (
              <div key={col.title}>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--gold)', marginBottom: 18 }}>{col.title}</div>
                {col.links.map(l => (
                  <Link key={l.label} to={l.href} className="footer-link" style={{ display: 'block', fontSize: 13, color: 'rgba(237,227,209,0.7)', textDecoration: 'none', marginBottom: 10, transition: 'color 0.18s ease' }}>{l.label}</Link>
                ))}
              </div>
            ))}
          </div>

          <div style={{ marginTop: 56, paddingTop: 24, borderTop: '1px solid rgba(237,227,209,0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11, color: 'rgba(237,227,209,0.45)', letterSpacing: '0.06em' }} className="footer-bottom">
            <span>© 2026 VritiEthnic · Crafted in Jaipur · Worn worldwide</span>
            <span>Secure payments via Razorpay · GST compliant</span>
          </div>
        </div>
      </footer>

      <style>{`
        .footer-link:hover { color: var(--gold) !important; }
        .nav-link:hover { color: var(--ink) !important; background: rgba(42,31,26,0.04) !important; }
        @media (max-width: 900px) {
          .desktop-nav { display: none !important; }
          .mobile-menu-btn { display: block !important; }
          .footer-grid { grid-template-columns: 1fr 1fr !important; gap: 32px !important; }
          .footer-bottom { flex-direction: column !important; gap: 12px !important; text-align: center !important; }
        }
        @media (max-width: 600px) {
          .footer-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </>
  );
}

function IconBtn({ children, onClick, label, count }) {
  return (
    <motion.button
      onClick={onClick}
      aria-label={label}
      whileTap={{ scale: 0.9 }}
      whileHover={{ scale: 1.06 }}
      style={{
        position: 'relative', width: 42, height: 42, display: 'flex', alignItems: 'center', justifyContent: 'center',
        border: 'none', background: 'transparent', color: 'var(--ink)', borderRadius: '50%', cursor: 'pointer',
      }}
    >
      {children}
      {count > 0 && (
        <motion.span
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 500, damping: 20 }}
          style={{
            position: 'absolute', top: 4, right: 2, minWidth: 18, height: 18,
            borderRadius: 999, background: 'var(--primary)', color: '#FFF',
            fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '0 4px', boxShadow: '0 2px 8px rgba(122,31,43,0.4)',
            border: '2px solid var(--bg)',
          }}
        >
          {count}
        </motion.span>
      )}
    </motion.button>
  );
}
